/**
 * Vesper Deduped Fetch (Shared)
 *
 * Tiny in-memory dedupe + TTL cache around read endpoints. Two layers:
 *
 *   1. In-flight dedup — if a fetch for `key` is already pending, the next
 *      caller receives the same promise instead of starting a duplicate
 *      request. Eliminates the rapid-mount fetch storms that happen when a
 *      user tab-switches fast (e.g., 116 /users/me requests in 3 seconds
 *      observed in e2e/.tab-switch-report.json before the rate-limit fix).
 *
 *   2. Time-bounded cache — once a fetch resolves, the value stays in memory
 *      for `ttlMs` and any caller during that window gets the cached value
 *      without hitting the network. Errors are not cached — the next caller
 *      retries normally.
 *
 * Cache invalidation: call `bust(key)` after a mutation that should make the
 * next read return fresh data. For parameterized keys (e.g., paginated feeds
 * keyed as `updates:feed:limit=10:offset=0`), use `bustByPrefix(prefix)` to
 * invalidate every key starting with that prefix.
 *
 * ── Race protection (per-key generation counter) ─────────────────────────
 *
 * Without it, a bust that fires while a fetch is in-flight would lose. The
 * in-flight fetch could resolve AFTER the bust and write its (now-stale)
 * value back into the cache:
 *
 *     T=0   fetch A starts, cache holds { promise: A }
 *     T=10  user mutates, bust('me') → cache.delete
 *     T=30  fetch A resolves (with pre-mutation data) → would re-cache stale
 *
 * To fix: every key has a generation counter. `bust()` increments it.
 * `deduped()` records the current generation when it starts the fetch and
 * only writes the resolved value back if the generation hasn't advanced
 * since. Stale-after-bust writes are silently dropped — the caller still
 * gets the resolved value (the request was already in flight, no point
 * throwing it away from their perspective), but it doesn't poison the cache
 * for future readers.
 *
 * Scope: opt-in. Only services that explicitly wrap their reads in `deduped()`
 * participate. Mutations and write endpoints intentionally bypass — they
 * always hit the network.
 *
 * NOT a replacement for SWR / React Query — there's no revalidation, no
 * focus-refetch, no stale-while-revalidate. Just enough to stop fetch
 * storms during normal navigation.
 */

interface CacheEntry<T> {
  /** Set while the fetch is in flight. Cleared on resolve/reject. */
  promise?: Promise<T>;
  /** Resolved value. `resolved` distinguishes a real undefined result (rare)
   *  from "no value yet." */
  value?: T;
  resolved: boolean;
  /** ms epoch at which the cached value goes stale. */
  expiresAt: number;
  /** Generation at the time this entry was created. Used to detect bust-
   *  during-in-flight: when the fetch resolves, it compares its startGen
   *  against the current key generation; mismatch → discard the write. */
  startGen: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cache = new Map<string, CacheEntry<any>>();
const generations = new Map<string, number>();

// LRU bound: parameterized keys (`updates:feed:limit=…:offset=…`,
// `geo:nearbyAll:lat:lng:r`) accumulate one entry per unique tuple. Without a
// cap, long sessions with infinite scroll or map panning grow the cache
// without bound. Map iteration order is insertion-order in JS, so we evict
// the oldest entry whenever we cross the cap. Re-inserting an existing key
// keeps it at its old position — `cache.set` updates value but not order —
// so we explicitly delete-then-set in the write path to refresh recency.
const MAX_CACHE_ENTRIES = 200;

function evictOldestIfNeeded(): void {
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
    // Note: we do NOT delete the generation counter for the evicted key.
    // If a stale in-flight fetch for that key resolves after eviction, the
    // generation check in `deduped()` still works correctly — its startGen
    // either matches (cache regrew while in flight) and writes back, or
    // doesn't (a bust happened) and discards. Generations leak slowly with
    // unique keys; periodic bustAll() on logout resets the whole map.
  }
}

/** Set a cache entry with LRU recency: delete-then-set so re-inserts move
 *  the key to the end of insertion order, making `keys().next()` always
 *  return the genuinely-oldest entry on eviction. */
function setEntry<T>(key: string, entry: CacheEntry<T>): void {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, entry);
  evictOldestIfNeeded();
}

function getGen(key: string): number {
  return generations.get(key) ?? 0;
}

function bumpGen(key: string): void {
  generations.set(key, getGen(key) + 1);
}

/**
 * Wrap a read function so concurrent + repeated calls within `ttlMs` collapse
 * into a single network request.
 *
 * @param key   Unique cache key. Use a stable string per logical resource.
 *              Include parameters (e.g., `'updates:feed:limit=10:offset=0'`)
 *              so different queries don't share state.
 * @param fn    Producer that performs the actual network fetch when needed.
 * @param ttlMs How long the resolved value stays fresh. Default 5s — short
 *              enough that profile/score updates feel live, long enough to
 *              absorb mount-bursts during tab cycling.
 */
export async function deduped<T>(
  key: string,
  fn: () => Promise<T>,
  ttlMs = 5_000,
): Promise<T> {
  const now = Date.now();
  const entry = cache.get(key) as CacheEntry<T> | undefined;

  // In-flight: a request for this key is already running. Share the promise.
  if (entry?.promise) return entry.promise;

  // Fresh cache: resolved value within TTL. Return without re-fetching.
  if (entry?.resolved && now < entry.expiresAt) return entry.value as T;

  // Cold or expired: start a new fetch and store its promise so concurrent
  // callers piggy-back instead of starting their own. Record the current
  // generation so we can detect a bust that races the fetch (see header).
  const startGen = getGen(key);
  const promise = fn().then(
    (value) => {
      // Only write back if no bust happened during the fetch. If startGen
      // is behind the current gen, a mutation has already invalidated this
      // result — caching it would re-introduce stale data for `ttlMs`.
      if (getGen(key) === startGen) {
        setEntry(key, {
          resolved: true,
          value,
          expiresAt: Date.now() + ttlMs,
          startGen,
        });
      }
      return value;
    },
    (err) => {
      // Do NOT cache errors — let the next caller retry. A failed 429 should
      // not poison reads for the next ttlMs.
      const cur = cache.get(key);
      if (cur && cur.startGen === startGen) cache.delete(key);
      throw err;
    },
  );
  setEntry(key, { promise, resolved: false, expiresAt: now + ttlMs, startGen });
  return promise;
}

/** Drop a single cache entry. Call after mutations that change the resource.
 *  Also bumps the per-key generation so any in-flight fetch resolving after
 *  this point won't re-cache its now-stale value. */
export function bust(key: string): void {
  bumpGen(key);
  cache.delete(key);
}

/** Drop every cache entry whose key starts with `prefix`. Use for
 *  parameterized resources (e.g., `bustByPrefix('updates:feed:')` to
 *  invalidate every page of the Updates feed after a post/save/delete). */
export function bustByPrefix(prefix: string): void {
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) {
      bumpGen(key);
      cache.delete(key);
    }
  }
  // Also bump generations for in-flight fetches whose keys start with prefix
  // but happen to share gen-state in the generations map.
  for (const key of Array.from(generations.keys())) {
    if (key.startsWith(prefix)) bumpGen(key);
  }
}

/** Drop every entry. Call on logout so the next session starts cold. Bumps
 *  every known generation so any in-flight fetch from the previous session
 *  doesn't write its result into the next session's cache. */
export function bustAll(): void {
  for (const key of Array.from(cache.keys())) bumpGen(key);
  cache.clear();
}
